import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Select, Space, Typography } from 'antd';

import {
  CREATING,
  HISTORICAL_SCENARIO,
  UnderlyingAsset,
  selectors,
  updateSessionSectorBenchmark,
} from 'state';

const ETF_RESOURCE_TYPES = [
  'ETF',
  'ETFEquities',
  'ETFBonds',
  'ETFCommodities',
] as const;

const PerSectorAlphaSettings: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const isHistorical = session.scenario_type === HISTORICAL_SCENARIO;

  const isCreating = session.status === CREATING;
  const sectorAlphaSettings = session.sector_alpha_settings || {
    playable_sectors: [],
    session_sectors: [],
  };

  const benchmarkBySector = useMemo(
    () =>
      sectorAlphaSettings.session_sectors.reduce<Record<string, number | null>>(
        (acc, row) => {
          acc[row.sector_name] = row.benchmark_asset_id;
          return acc;
        },
        {}
      ),
    [sectorAlphaSettings.session_sectors]
  );

  const etfOptions = useMemo(() => {
    const options = session.underlying_assets.reduce<UnderlyingAsset[]>(
      (acc, assetId) => {
        const asset = underlyingAssets[assetId];
        if (
          asset &&
          ETF_RESOURCE_TYPES.includes(
            asset.resourcetype as (typeof ETF_RESOURCE_TYPES)[number]
          )
        ) {
          acc.push(asset);
        }
        return acc;
      },
      []
    );

    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [session.underlying_assets, underlyingAssets]);

  if (!isHistorical) {
    return null;
  }

  return (
    <div className="psa-root">
      {!session.per_sector_alpha ? null : (
        <div className="psa-body">
          <Typography.Title level={3}>
            Per-sector alpha monitoring
          </Typography.Title>
          {sectorAlphaSettings.playable_sectors.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No playable sectors yet. Select and save session instruments."
            />
          ) : (
            <Space
              className="psa-list"
              direction="vertical"
              size={12}
              style={{ width: '100%' }}
            >
              {sectorAlphaSettings.playable_sectors.map(sectorName => (
                <div key={sectorName}>
                  <Typography.Text type="secondary">
                    {sectorName}
                  </Typography.Text>
                  <Select
                    showSearch
                    allowClear
                    style={{ width: '100%', marginTop: 4 }}
                    placeholder="Select benchmark ETF"
                    disabled={!isCreating}
                    value={benchmarkBySector[sectorName] ?? undefined}
                    optionFilterProp="children"
                    notFoundContent="You haven't selected any ETF instruments for this session"
                    onChange={value => {
                      dispatch(
                        updateSessionSectorBenchmark({
                          session_id: session.id,
                          sector_name: sectorName,
                          benchmark_asset_id: value ?? null,
                        })
                      );
                    }}
                  >
                    {etfOptions.map(asset => (
                      <Select.Option key={asset.id} value={asset.id}>
                        {asset.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              ))}
            </Space>
          )}
        </div>
      )}
    </div>
  );
};

export default PerSectorAlphaSettings;
